import { z } from 'zod';
import { itemTipo } from './itens.js';
import { cuid, isoDate } from './primitives.js';
import type { StatusOrcamentoDto } from './orcamentos.js';

/**
 * Painel de margens: GET /margens/*. Tudo e agregado no servidor (SQL) sobre orcamentos gravados
 * (saidas do motor). Valores ja arredondados para exibir: dinheiro 2 casas, fracao 4 casas, strings decimais.
 */
export const tipoItemMargens = itemTipo;

/** Filtros comuns. Sem `status`, o painel olha tudo menos PERDIDO. `EM_ABERTO` = RASCUNHO, ENVIADO e EM_NEGOCIACAO. */
export const filtrosMargens = {
  /** Periodo, inclusivo nas duas pontas, pela data do orcamento (padrao) ou pela data da APROVACAO (`dataRef`; so orcamentos aprovados tem data de aprovacao). */
  dataDe: isoDate.optional(),
  dataAte: isoDate.optional(),
  /** Qual data o periodo filtra: ORCAMENTO (padrao) ou APROVACAO (o mesmo criterio do ponto de equilibrio: receita do periodo = vendas aprovadas no periodo). */
  dataRef: z.enum(['ORCAMENTO', 'APROVACAO']).optional(),
  tabelaId: cuid.optional(),
  /** Orcamentos com ao menos um item do tipo; nas visoes por item, so os itens do tipo. */
  tipo: tipoItemMargens.optional(),
  /** Categoria do item (com as subcategorias) ou `SEM` (Sessao 28): mesma regra do tipo (orcamentos com ao menos um item da categoria; nas visoes por item, so os itens). */
  categoriaId: z.union([cuid, z.literal('SEM')]).optional(),
  status: z.enum(['APROVADO', 'EM_ABERTO']).optional(),
  clienteId: cuid.optional(),
  /** Origem do orcamento. Padrao APP: os totais importados do XARP nao sao reproduziveis (DP5) e ficam fora do painel; `TODAS` os inclui. */
  origem: z.enum(['APP', 'IMPORTADO_XARP', 'TODAS']).optional(),
};
export const margensFiltrosSchema = z.object(filtrosMargens);
export type MargensFiltros = z.infer<typeof margensFiltrosSchema>;

const direcao = z.enum(['asc', 'desc']).default('asc');
const paginaMargens = {
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
};

export const margensOrcamentosSchema = z.object({
  ...filtrosMargens,
  /** Quantos orcamentos (os de menor lucro primeiro). */
  limit: z.coerce.number().int().min(1).max(200).default(30),
});
export type MargensOrcamentosQuery = z.infer<typeof margensOrcamentosSchema>;

export const ordenarGrupoMargens = z.enum(['rotulo', 'quantidade', 'receita', 'lucroMedio', 'abaixoDaMeta', 'mcPct']);
export const margensGrupoSchema = z.object({
  ...filtrosMargens,
  ...paginaMargens,
  ordenar: ordenarGrupoMargens.default('lucroMedio'),
  direcao,
});
export type MargensGrupoQuery = z.infer<typeof margensGrupoSchema>;

export const ordenarItensAbaixo = z.enum(['lucro', 'receita', 'codigo', 'orcamento']);
export const margensItensAbaixoSchema = z.object({
  ...filtrosMargens,
  ...paginaMargens,
  /** `meta`: faixa laranja ou vermelha; `minimo`: so a vermelha. */
  nivel: z.enum(['meta', 'minimo']).default('meta'),
  ordenar: ordenarItensAbaixo.default('lucro'),
  direcao,
});
export type MargensItensAbaixoQuery = z.infer<typeof margensItensAbaixoSchema>;

export type FaixaMargem = 'green' | 'orange' | 'red';

export interface MargensResumoResponse {
  /** Orcamentos considerados (apos filtros). */
  orcamentos: number;
  abaixoDaMeta: number;
  abaixoDoMinimo: number;
  exibicao: {
    /** Soma de `total` dos orcamentos considerados. */
    receita: string;
    /** Soma de `total` dos orcamentos abaixo da meta. */
    receitaAbaixoDaMeta: string;
    /** Lucro medio ponderado pela receita bruta (fracao, 4 casas); null sem orcamentos com lucro. */
    lucroMedio: string | null;
    /** Meta/minimo quando todos os orcamentos considerados compartilham o mesmo valor; senao null. */
    metaLucro: string | null;
    minimoLucro: string | null;
  };
  /**
   * Itens (produtos) com preco praticado abaixo do sugerido, pela ultima formacao gravada e o ultimo praticado
   * de cada item x tabela x condicao (posicao atual: ignora o periodo e o status).
   */
  praticadoAbaixoDoSugerido: { produtos: number; base: number };
  /**
   * Margem de contribuicao media dos orcamentos considerados (mesma regra do ponto de equilibrio): ponderada pela receita sem IPI; MC gravada na linha
   * ou, nos antigos, calculada do snapshot. `foraDoCalculo` = orcamentos sem o custo separado (materiais/aquisicao x processo): nao entram na MC%.
   */
  mc: { pct: string | null; usados: number; deSnapshot: number; foraDoCalculo: number };
}

export interface MargensOrcamentoLinha {
  id: string;
  numero: number;
  status: StatusOrcamentoDto;
  tabelaId: string;
  tabelaNome: string;
  clienteId: string | null;
  clienteNome: string | null;
  faixa: FaixaMargem | null;
  exibicao: {
    data: string;
    receita: string;
    lucro: string;
    metaLucro: string;
    minimoLucro: string;
    /** Fracao 0..1 da largura da barra na escala (`escala.maximo`). */
    barra: string;
    posicaoMeta: string;
    posicaoMinimo: string;
    /** Sessao 15: MC% do orcamento (itens); null sem MC gravada. */
    mcPct: string | null;
  };
}

export interface MargensOrcamentosResponse {
  itens: MargensOrcamentoLinha[];
  /** Orcamentos com lucro (apos filtros), independente do limite. */
  total: number;
  limit: number;
  /** Escala das barras (fracao do lucro que enche a barra) e marcas comuns (null se variam entre as linhas). */
  escala: {
    maximo: string;
    meta: { valor: string; posicao: string } | null;
    minimo: { valor: string; posicao: string } | null;
  };
}

export interface MargensGrupoLinha {
  /** Id do grupo (tabela, cliente, item ou tipo); '' para "sem cliente". */
  chave: string;
  rotulo: string;
  /** Codigo do item (so por produto). */
  codigo: string | null;
  tipo: z.infer<typeof tipoItemMargens> | null;
  /** Orcamentos (por tabela/cliente/mes) ou orcamentos distintos que contem o item/tipo. */
  quantidade: number;
  abaixoDaMeta: number;
  abaixoDoMinimo: number;
  exibicao: {
    receita: string;
    /** Lucro medio ponderado pela receita (fracao, 4 casas); null sem lucro. */
    lucroMedio: string | null;
    /** Largura da barra (fracao 0..1 da escala de 30%). */
    barra: string | null;
    /** Meta e minimo medios do grupo (ponderados pela mesma receita); a `faixa` compara o lucro medio com eles. */
    metaLucro: string | null;
    minimoLucro: string | null;
    /** Sessao 15: margem de contribuicao media (MC / receita sem IPI, fracao); null sem MC gravada (versoes anteriores). */
    mcPct: string | null;
  };
  faixa: FaixaMargem | null;
}

export interface MargensGrupoResponse {
  itens: MargensGrupoLinha[];
  total: number;
  limit: number;
  offset: number;
}

export interface MargensItemAbaixoLinha {
  orcamentoId: string;
  orcamentoNumero: number;
  seq: number;
  itemId: string;
  codigo: string;
  descricao: string;
  tipo: z.infer<typeof tipoItemMargens>;
  tabelaNome: string;
  clienteNome: string | null;
  faixa: FaixaMargem;
  exibicao: { quantidade: string; receita: string; lucro: string; metaLucro: string; minimoLucro: string };
}

export interface MargensItensAbaixoResponse {
  itens: MargensItemAbaixoLinha[];
  total: number;
  limit: number;
  offset: number;
}

export interface MargensEvolucaoPonto {
  /** 'AAAA-MM'. */
  mes: string;
  orcamentos: number;
  abaixoDaMeta: number;
  exibicao: { receita: string; lucroMedio: string | null; barra: string | null };
  faixa: FaixaMargem | null;
}
export interface MargensEvolucaoResponse {
  pontos: MargensEvolucaoPonto[];
}
